import _ from 'lodash';
import React from 'react';
import { connect, type ConnectedProps } from 'react-redux';

import { LOCALSTORAGE_KEYS, URLS, LINKS } from '@/constants';
import { USE_ANCAP_UPLIFT } from '@/constants/flags';
import Footer from '@/components/footer';
import Header from '@/components/header';
import { iconNames } from '@/components/icons';
import MaintenanceBanner from '@/components/maintenance-banner';
import MiniCartWrapper from '@/components/mini-cart-wrapper';
import Loading from '@/components/loading';
import { SEO, type SEOProps } from '@/components/seo';
import { cn } from '@/lib/utils';
import { CompareProvider } from '@/context/compare';
import { useMaintenanceBanner } from '@/context/maintenance-banner';
import { hydrateVehicleIdsStateFromObject } from '@/utils/hydrate-vehicle-ids-state';
import { type Dispatch, type RootState } from '@/app-state/store';

const SECONDARY_TITLE = 'More about';

const SOCIAL_LINKS = [
  { iconName: iconNames.facebook, url: URLS.FACEBOOK },
  { iconName: iconNames.instagram, url: URLS.INSTAGRAM },
  { iconName: iconNames.twitter, url: URLS.TWITTER },
];

// @ts-expect-error [🤖 PLEASE FIX 🤖]: Parameter 'compareVehicleIds' implicitly has an 'any' type.
const hydrateCompareVehicleIds = (compareVehicleIds, actions) => {
  if (!compareVehicleIds || !compareVehicleIds.length) {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const storageItem = localStorage.getItem(LOCALSTORAGE_KEYS.COMPARE);
      const vehicleIds = storageItem ? JSON.parse(storageItem) : [];
      hydrateVehicleIdsStateFromObject(vehicleIds, actions);
    }
  }
};

const mapState = (state: RootState) => ({
  compareVehicleIds: state.compare.compareVehicleIds,
  isOpen: state.ui.global.isMiniCartOpen,
});

const mapDispatch = (dispatch: Dispatch) => ({
  actions: {
    addToCompare: dispatch.compare.addToCompare,
    closeMiniCart: dispatch.ui.closeMiniCart,
    openMiniCart: dispatch.ui.openMiniCart,
    removeFromCompare: dispatch.compare.removeFromCompare,
  },
});

const connector = connect(mapState, mapDispatch);

type LayoutProps = ConnectedProps<typeof connector> & {
  children: React.ReactNode;
  className?: string;

  isOpen?: boolean;
  compareVehicleIds?: string[];
  seo?: SEOProps;
};

const Layout = ({
  //@ts-expect-error: TODO type actions
  actions,
  children,
  className = '',
  compareVehicleIds = [],
  isOpen = false,
  seo,
}: LayoutProps) => {
  const { closeMiniCart, openMiniCart, removeFromCompare } = actions;
  const maintenanceBanner = useMaintenanceBanner();
  const numOfVehiclesToCompare = _.size(compareVehicleIds);

  hydrateCompareVehicleIds(compareVehicleIds, actions);

  return (
    <CompareProvider>
      <div className={cn('flex min-h-screen w-full flex-col justify-center bg-greyscale-25 content-center', className)}>
        {seo && <SEO {...seo} />}
        <Loading />
        {!USE_ANCAP_UPLIFT && (
          <MiniCartWrapper {...{ compareVehicleIds, isOpen, removeFromCompare }} onClose={closeMiniCart} />
        )}
        <Header
          compareButtonProps={
            USE_ANCAP_UPLIFT ? undefined : { count: numOfVehiclesToCompare, onOpenComparison: openMiniCart }
          }
          primaryNavProps={{ links: LINKS.PRIMARY }}
          secondaryNavProps={{ links: LINKS.SECONDARY, navTitle: SECONDARY_TITLE }}
          socialLinksProps={{ socialIcons: SOCIAL_LINKS, tagline: 'Join the conversation' }}
        />
        <MaintenanceBanner banner={maintenanceBanner} />
        {children}
        <Footer
          {...{
            termsAndPrivacyLinks: LINKS.TERMS_AND_PRIVACY,
            socialLinks: SOCIAL_LINKS,
            pageLinks: [...LINKS.PRIMARY, ...LINKS.SECONDARY],
          }}
        />
      </div>
    </CompareProvider>
  );
};

export default connector(Layout);
